import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../hooks/useAdmin';
import { useTranslation } from '../../hooks/useTranslation';
import {
  AdminPageHeader,
  AdminTable,
  AdminEmptyState,
  type AdminTableColumn,
} from '../../components/admin';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import type { DiscountCodeEntry } from '@flymind/shared';

/* ---------- Constants ---------- */

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-700',
  expired: 'bg-red-100 text-red-700',
};

/* ---------- Helpers ---------- */

function getStatus(d: DiscountCodeEntry): string {
  if (!d.active) return 'inactive';
  if (new Date(d.validUntil) < new Date()) return 'expired';
  return 'active';
}

function toInputDate(d: Date | string | undefined): string {
  if (!d) return '';
  const date = new Date(d);
  return date.toISOString().split('T')[0];
}

/* ---------- Form state ---------- */

interface DiscountFormData {
  code: string;
  description: string;
  discountPercent: number;
  maxUses: number | '';
  validFrom: string;
  validUntil: string;
  applicablePlans: string[];
}

const EMPTY_FORM: DiscountFormData = {
  code: '',
  description: '',
  discountPercent: 10,
  maxUses: '',
  validFrom: '',
  validUntil: '',
  applicablePlans: [],
};

/* ---------- Component ---------- */

export default function AdminDiscounts() {
  const { adminApi } = useAdmin();
  const { t, locale } = useTranslation();
  const dateLocale = locale === 'en' ? 'en-US' : 'it-IT';

  const PLAN_OPTIONS = [
    { value: 'premium', label: t('admin.discounts.planPro') },
    { value: 'unlimited', label: t('admin.discounts.planUnlimited') },
  ];

  function getStatusLabel(status: string): string {
    switch (status) {
      case 'active':
        return t('admin.discounts.statusActive');
      case 'inactive':
        return t('admin.discounts.statusInactive');
      case 'expired':
        return t('admin.discounts.statusExpired');
      default:
        return status;
    }
  }

  function formatDate(d: Date | string): string {
    return new Date(d).toLocaleDateString(dateLocale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  const [discounts, setDiscounts] = useState<DiscountCodeEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<DiscountFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<DiscountCodeEntry | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  /* ---------- Fetch ---------- */

  const fetchDiscounts = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminApi.get<DiscountCodeEntry[]>('/discounts');
      setDiscounts(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('Errore nel caricamento codici sconto:', err);
    } finally {
      setLoading(false);
    }
  }, [adminApi]);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  /* ---------- Modal open / close ---------- */

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (d: DiscountCodeEntry) => {
    setEditingId(d.id);
    setForm({
      code: d.code,
      description: d.description || '',
      discountPercent: d.discountPercent,
      maxUses: d.maxUses ?? '',
      validFrom: toInputDate(d.validFrom),
      validUntil: toInputDate(d.validUntil),
      applicablePlans: d.applicablePlans ?? [],
    });
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setFormError(null);
  };

  /* ---------- Save ---------- */

  const handleSave = async () => {
    // Validate
    if (!form.code.trim()) {
      setFormError(t('admin.discounts.errCodeRequired'));
      return;
    }
    if (form.discountPercent < 1 || form.discountPercent > 100) {
      setFormError(t('admin.discounts.errPercentRange'));
      return;
    }
    if (!form.validFrom || !form.validUntil) {
      setFormError(t('admin.discounts.errDatesRequired'));
      return;
    }

    setSaving(true);
    setFormError(null);

    try {
      const body: Record<string, unknown> = {
        code: form.code.trim().toUpperCase(),
        description: form.description.trim() || null,
        discountPercent: form.discountPercent,
        maxUses: form.maxUses === '' ? null : Number(form.maxUses),
        validFrom: form.validFrom,
        validUntil: form.validUntil,
        applicablePlans: form.applicablePlans,
      };

      if (editingId) {
        await adminApi.patch(`/discounts/${editingId}`, body);
      } else {
        await adminApi.post('/discounts', body);
      }

      closeModal();
      await fetchDiscounts();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('admin.discounts.errSaveGeneric');
      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Toggle active ---------- */

  const handleToggleActive = async (e: React.MouseEvent, d: DiscountCodeEntry) => {
    e.stopPropagation();
    try {
      await adminApi.patch(`/discounts/${d.id}`, { active: !d.active });
      await fetchDiscounts();
    } catch (err) {
      console.error('Errore nel cambio stato:', err);
    }
  };

  /* ---------- Delete ---------- */

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await adminApi.delete(`/discounts/${deleteTarget.id}`);
      setDeleteTarget(null);
      await fetchDiscounts();
    } catch (err) {
      console.error('Errore nella disattivazione:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  /* ---------- Plan checkbox toggle ---------- */

  const togglePlan = (plan: string) => {
    setForm((prev) => ({
      ...prev,
      applicablePlans: prev.applicablePlans.includes(plan)
        ? prev.applicablePlans.filter((p) => p !== plan)
        : [...prev.applicablePlans, plan],
    }));
  };

  /* ---------- Table columns ---------- */

  const columns: AdminTableColumn<DiscountCodeEntry>[] = [
    {
      key: 'code',
      label: t('admin.discounts.colCode'),
      render: (row) => (
        <span className="font-mono font-semibold text-primary-600">{row.code}</span>
      ),
    },
    {
      key: 'discountPercent',
      label: t('admin.discounts.colDiscount'),
      render: (row) => <span>{row.discountPercent}%</span>,
    },
    {
      key: 'currentUses',
      label: t('admin.discounts.colUses'),
      render: (row) => (
        <span>
          {row.currentUses}/{row.maxUses ?? '\u221e'}
        </span>
      ),
    },
    {
      key: 'validity',
      label: t('admin.discounts.colValidity'),
      render: (row) => (
        <span className="text-text-secondary text-xs">
          {formatDate(row.validFrom)} - {formatDate(row.validUntil)}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('admin.discounts.colStatus'),
      render: (row) => {
        const status = getStatus(row);
        return (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[status] || ''}`}
          >
            {getStatusLabel(status)}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: t('admin.discounts.colActions'),
      render: (row) => (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => openEdit(row)}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            {t('admin.discounts.edit')}
          </button>
          <button
            onClick={(e) => handleToggleActive(e, row)}
            className={`text-sm font-medium ${
              row.active
                ? 'text-amber-600 hover:text-amber-700'
                : 'text-green-600 hover:text-green-700'
            }`}
          >
            {row.active ? t('admin.discounts.deactivate') : t('admin.discounts.activate')}
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            className="text-sm font-medium text-red-600 hover:text-red-700"
          >
            {t('admin.discounts.delete')}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={t('admin.discounts.title')}
        subtitle={t('admin.discounts.totalCount').replace('{count}', String(discounts.length))}
        actions={
          <Button size="sm" onClick={openCreate}>
            {t('admin.discounts.newCode')}
          </Button>
        }
      />

      {/* Table */}
      {!loading && discounts.length === 0 ? (
        <AdminEmptyState
          title={t('admin.discounts.emptyTitle')}
          description={t('admin.discounts.emptyDescription')}
        />
      ) : (
        <AdminTable
          columns={columns}
          data={discounts}
          loading={loading}
        />
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative w-full max-w-lg rounded-xl bg-surface border border-border p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              {editingId ? t('admin.discounts.modalTitleEdit') : t('admin.discounts.modalTitleNew')}
            </h2>

            {formError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {formError}
              </div>
            )}

            <div className="space-y-4">
              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  {t('admin.discounts.fieldCode')}
                </label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder={t('admin.discounts.codePlaceholder')}
                  disabled={!!editingId}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary font-mono uppercase placeholder:text-text-secondary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  {t('admin.discounts.fieldDescription')}
                </label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={t('admin.discounts.descriptionPlaceholder')}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              {/* Discount percent */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  {t('admin.discounts.fieldDiscountPercent')}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={form.discountPercent}
                    onChange={(e) =>
                      setForm({ ...form, discountPercent: Number(e.target.value) })
                    }
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 pr-8 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-secondary">
                    %
                  </span>
                </div>
              </div>

              {/* Max uses */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  {t('admin.discounts.fieldMaxUses')}
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.maxUses}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      maxUses: e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                  placeholder={t('admin.discounts.maxUsesPlaceholder')}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              {/* Date range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    {t('admin.discounts.fieldValidFrom')}
                  </label>
                  <input
                    type="date"
                    value={form.validFrom}
                    onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    {t('admin.discounts.fieldValidUntil')}
                  </label>
                  <input
                    type="date"
                    value={form.validUntil}
                    onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Applicable plans */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  {t('admin.discounts.fieldApplicablePlans')}
                </label>
                <div className="flex flex-wrap gap-3">
                  {PLAN_OPTIONS.map((plan) => (
                    <label
                      key={plan.value}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={form.applicablePlans.includes(plan.value)}
                        onChange={() => togglePlan(plan.value)}
                        className="h-4 w-4 rounded border-border text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-text-primary">{plan.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex gap-3 justify-end">
              <Button variant="outline" onClick={closeModal} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} isLoading={saving}>
                {editingId ? t('admin.discounts.saveChanges') : t('admin.discounts.createCode')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        title={t('admin.discounts.deleteTitle')}
        message={
          <>
            {t('admin.discounts.deleteMessagePrefix')}{' '}
            <span className="font-mono font-bold">{deleteTarget?.code}</span>?{' '}
            {t('admin.discounts.deleteMessageSuffix')}
          </>
        }
        confirmLabel={t('admin.discounts.deleteConfirm')}
        variant="danger"
        isLoading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
